import { css } from '@emotion/react';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import NvTooltip from 'shared/components/nv-tooltip';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { gray5 } from 'styles/global_defaults/colors';
import { mobile } from 'styles/global_defaults/media-queries';
import { halfSpacing, quarterSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { ReactComponent as ProgramEngagementReportingGraphic } from 'styles/icons/program-engagement-reporting.svg';
import { ReactComponent as SessionPulseChecksReportingGraphic } from 'styles/icons/session-pulse-checks-reporting.svg';


const ProgramEngagementPopover = ({ aliases }) => {
  const translationBase = t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER;
  const reportData = [
    translationBase.MENTOR_NAME(aliases),
    translationBase.MENTEE_NAME(aliases),
    translationBase.MATCHED_DATE(),
    translationBase.MEETING_PLAN_CADENCE(),
    translationBase.MEETING_PLAN_ENDS_DATE(),
    translationBase.MEETING_PLAN_DURATION(),
    translationBase.SESSIONS_TOTAL(),
    translationBase.SESSIONS_PAST(),
    translationBase.SESSIONS_UPCOMING(),
    translationBase.ACTION_ITEMS_TOTAL(),
    translationBase.ACTION_ITEMS_OUTSTANDING(),
    translationBase.ACTION_ITEMS_COMPLETE(),
    translationBase.ACTION_ITEMS_TOTAL_MENTOR_OWNED(aliases),
    translationBase.ACTION_ITEMS_TOTAL_MENTEE_OWNED(aliases),
    translationBase.MENTOR_LAST_VISIT(aliases),
    translationBase.MENTEE_LAST_VISIT(aliases),
    translationBase.LAST_SESSION_DATE(),
  ];

  return (
    <div>
      <div className='gray-3 font-weight-bold mb-2'>
        {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD_INCLUDES()}
      </div>
      <ul className='pl-4 mb-0'>
        {reportData.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  );
};


const PulseChecksPopover = ({ aliases }) => {
  const translationBase = t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER;
  const reportData = [
    translationBase.MENTOR_NAME(aliases),
    translationBase.MENTEE_NAME(aliases),
    translationBase.SESSION_DATE(),
    translationBase.MENTEE_RESPONSE(aliases),
    translationBase.MENTOR_RESPONSE(aliases),
    translationBase.MENTEE_RESPONSE_DATE(aliases),
    translationBase.MENTOR_RESPONSE_DATE(aliases),
  ];

  return (
    <div>
      <div className='gray-3 font-weight-bold mb-2'>
        {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD_INCLUDES()}
      </div>
      <ul className='pl-4 mb-0'>
        {reportData.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const Reporting = () => {
  const styles = css`
    .download-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: ${threeQuartersSpacing}px;

      ${mobile(css`
        grid-template-columns: 1fr;
      `)}
    }

    .download-card {
      border-radius: ${quarterSpacing}px;
      border: 1px solid ${gray5};
      padding: ${halfSpacing}px;
    }
  `;

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram?.id));

  return (
    <div css={styles}>
      <div className='heading-3 mb-4'>
        {t.MENTORING_PROGRAMS.REPORTING.DATA_DOWNLOADS()}
        <NvTooltip text={t.MENTORING_PROGRAMS.REPORTING.DATA_DOWNLOADS_TOOLTIP()} placement='top'>
          <NvIcon icon='info' size='smallest' className='d-inline gray-3 ml-1' />
        </NvTooltip>
      </div>
      <div className='download-grid'>
        <div className='download-card'>
          <div className='d-flex justify-content-between align-items-center m-2'>
            <div className='font-weight-bold mr-4'>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT()}</div>
            <Button
              variant='secondary'
              className='d-flex align-items-center'
              href={`/mentorship_programs/${mentoringProgram?.id}/download_report?report_type=program_engagement`}
              target='_blank'
              aria-label={t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_ARIA_LABEL()}
              data-qa={config.pendo.athena.mentorshipProgram.reporting.downloadProgramEngagementReport}
            >
              <NvIcon icon='download' size='smallest' className='mr-1' />
              {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD()}
            </Button>
          </div>
          <div className='d-flex justify-content-between mt-4'>
            <div className='d-flex flex-column justify-content-between m-2'>
              <div className='text-body gray-2'>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_DESCRIPTION()}</div>
              <div className='d-flex font-weight-bold text-small'>
                {t.MENTORING_PROGRAMS.REPORTING.WHATS_INCLUDED()}
                <NvPopover
                  showOnHover
                  placement='top'
                  content={<ProgramEngagementPopover aliases={mentoringProgramAliases} />}
                >
                  <NvIcon icon='info' size='smallest' className='gray-3 ml-1' />
                </NvPopover>
              </div>
            </div>
            <div>
              <ProgramEngagementReportingGraphic
                height={75}
                width={110}
              />
            </div>
          </div>
        </div>

        <div className='download-card'>
          <div className='d-flex justify-content-between align-items-center m-2'>
            <div className='font-weight-bold mr-4'>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS()}</div>
            <Button
              variant='secondary'
              className='d-flex align-items-center'
              href={`/mentorship_programs/${mentoringProgram?.id}/download_report?report_type=pulse_check`}
              target='_blank'
              aria-label={t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_ARIA_LABEL()}
              data-qa={config.pendo.athena.mentorshipProgram.reporting.downloadPulseChecksReport}
            >
              <NvIcon icon='download' size='smallest' className='mr-1' />
              {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD()}
            </Button>
          </div>
          <div className='d-flex justify-content-between mt-4'>
            <div className='d-flex flex-column justify-content-between m-2'>
              <div className='text-body gray-2'>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_DESCRIPTION()}</div>
              <div className='d-flex font-weight-bold text-small'>
                {t.MENTORING_PROGRAMS.REPORTING.WHATS_INCLUDED()}
                <NvPopover
                  showOnHover
                  placement='top'
                  content={<PulseChecksPopover aliases={mentoringProgramAliases} />}
                >
                  <NvIcon icon='info' size='smallest' className='gray-3 ml-1' />
                </NvPopover>
              </div>
            </div>
            <div>
              <SessionPulseChecksReportingGraphic
                height={80}
                width={110}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default Reporting;
